import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  IconButton,
  Modal,
  Stack,
  Text,
} from "@fidelix/fx-miranda";
import {
  Heading,
  ListBox,
  ListBoxItem,
  Selection,
} from "react-aria-components";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import {
  GRAPHIC_CATEGORY_TYPES,
  GraphicCategoryType,
} from "./graphics-category.model";
import { setGraphicPictureCategories } from "./graphics.mutation";
import { CategoryIcon } from "../tree/tree-render.utils";
import { useRequiredParams } from "../utils/url";

export type SelectedPictureItem = {
  treeId: string;
  graphicsId: number;
  menuItemId: number;
  name?: string;
};

type ApplyProcessTypeDialogProps = {
  treeId?: string;
  treeIds?: string[];
  initialCategories?: GraphicCategoryType[];
  selectedPictures?: SelectedPictureItem[];
  onClose: () => void;
  onSuccess?: () => void;
};

function getSelectedCategories(selectedKeys: Selection): GraphicCategoryType[] {
  return GRAPHIC_CATEGORY_TYPES.filter((category) =>
    selectedKeys === "all" ? true : selectedKeys.has(String(category)),
  );
}

export function ApplyProcessTypeDialog({
  treeId,
  treeIds,
  initialCategories,
  selectedPictures,
  onClose,
  onSuccess,
}: ApplyProcessTypeDialogProps) {
  const isMulti = Boolean(
    (treeIds && treeIds.length > 0) ||
      (selectedPictures && selectedPictures.length > 0),
  );
  const queryClient = useQueryClient();
  const { companyId } = useRequiredParams({ companyId: "number" });

  const previousCategories = initialCategories;
  const [isPending, setIsPending] = useState(false);
  const [isError, setIsError] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Selection>(() =>
    !isMulti && initialCategories?.length
      ? new Set(initialCategories.map(String))
      : new Set(),
  );

  useEffect(() => {
    if (isMulti || !previousCategories?.length) {
      return;
    }
    setSelectedKeys(new Set(previousCategories.map(String)));
  }, [isMulti, previousCategories]);

  const selectedCategories = getSelectedCategories(selectedKeys);

  const onApply = async () => {
    const targetTreeIds = treeIds?.length
      ? treeIds
      : selectedPictures?.length
        ? selectedPictures.map((pic) => pic.treeId)
        : treeId
          ? [treeId]
          : [];

    if (targetTreeIds.length > 0) {
      setIsPending(true);
      setIsError(false);
      try {
        await setGraphicPictureCategories({
          companyId,
          treeIds: targetTreeIds,
          categories: selectedCategories,
        });
        queryClient.invalidateQueries({
          queryKey: ["tree"],
        });
        onSuccess?.();
        onClose();
      } catch {
        setIsError(true);
      } finally {
        setIsPending(false);
      }
    }
  };

  function getGraphicCategoryTypeLabel(category: GraphicCategoryType): string {
    switch (category) {
      case GraphicCategoryType.Heating:
        return t`Heating`;
      case GraphicCategoryType.Ventilation:
        return t`Ventilation`;
      case GraphicCategoryType.Cooling:
        return t`Cooling`;
      case GraphicCategoryType.Other:
        return t`Other`;
    }
  }

  return (
    <Modal onClose={onClose}>
      <Modal.Body>
        <DialogShell axis="y" spacing="none">
          <ModalHeader justify="space-between" align="flex-start">
            <ModalHeading slot="title">
              <Text variant="title2Bold">
                {isMulti ? (
                  <Trans>Apply process type to all selected images</Trans>
                ) : (
                  <Trans>Apply process type</Trans>
                )}
              </Text>
            </ModalHeading>
            <IconButton icon="close" variant="transparent" onPress={onClose} />
          </ModalHeader>

          {isMulti ? (
            <NoteRow align="center">
              <Text variant="body">
                <Trans>
                  Note! This will override any existing process types in the
                  selected images.
                </Trans>
              </Text>
            </NoteRow>
          ) : (
            <PreviousSelectionRow align="center" spacing="small">
              <Text variant="body">
                <Trans>Previous selection</Trans>:
              </Text>
              <Stack axis="x" spacing="xsmall" align="center" wrap="wrap">
                {previousCategories?.length ? (
                  previousCategories.map((category) => (
                    <CategoryChip key={category}>
                      <CategoryIcon iconType={category} />
                      <Text variant="body">
                        {getGraphicCategoryTypeLabel(category)}
                      </Text>
                    </CategoryChip>
                  ))
                ) : (
                  <Text variant="body">—</Text>
                )}
              </Stack>
            </PreviousSelectionRow>
          )}

          <Divider />

          <Content axis="y" spacing="normal">
            <Text variant="subtitle1Bold">
              <Trans>Categories</Trans>
            </Text>
            <CategoryListBox
              aria-label={t`Categories`}
              selectionMode="multiple"
              selectedKeys={selectedKeys}
              onSelectionChange={setSelectedKeys}
            >
              {GRAPHIC_CATEGORY_TYPES.map((category) => (
                <CategoryItem
                  key={category}
                  id={String(category)}
                  textValue={getGraphicCategoryTypeLabel(category)}
                >
                  <Stack axis="x" spacing="normal" align="center">
                    <Checkbox
                      aria-hidden
                      isSelected={selectedCategories.includes(category)}
                      style={{ pointerEvents: "none" }}
                    />
                    <CategoryIcon iconType={category} />
                    <Text variant="body">
                      {getGraphicCategoryTypeLabel(category)}
                    </Text>
                  </Stack>
                </CategoryItem>
              ))}
            </CategoryListBox>
          </Content>
          <Stack justify={"center"}>
            {isError && (
              <Text variant="bodyLight" color="error">
                <Trans>Something went wrong</Trans>
              </Text>
            )}
          </Stack>
        </DialogShell>
      </Modal.Body>
      <Modal.Footer>
        <FooterRow spacing="normal" justify="flex-end">
          <Button variant="text" onPress={onClose} isDisabled={isPending}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onPress={onApply} isDisabled={isPending}>
            <Trans>Apply</Trans>
          </Button>
        </FooterRow>
      </Modal.Footer>
    </Modal>
  );
}

const DialogShell = styled(Stack)`
  width: 62.5vw;
  min-width: 400px;
`;

const ModalHeader = styled(Stack)`
  padding: ${(p) => p.theme.space("large")} ${(p) => p.theme.space("large")}
    ${(p) => p.theme.space("normal")};
`;

const ModalHeading = styled(Heading)`
  margin: 0;
`;

const PreviousSelectionRow = styled(Stack)`
  padding: 0 ${(p) => p.theme.space("large")} ${(p) => p.theme.space("normal")};
  flex-wrap: wrap;
`;

const NoteRow = styled(Stack)`
  padding: 0 ${(p) => p.theme.space("large")} ${(p) => p.theme.space("normal")};
`;

const CategoryChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.theme.space("xsmall")};
  padding: ${(p) => p.theme.space("xxsmall")} ${(p) => p.theme.space("small")};
  border-radius: ${(p) => p.theme.radius("small")};
  background-color: ${(p) => p.theme.colors.mutedNeutralBackground};
  color: ${(p) => p.theme.colors.black};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${(p) => p.theme.colors.lightNeutral};
  margin: 0;
  width: 100%;
`;

const Content = styled(Stack)`
  padding: ${(p) => p.theme.space("normal")} ${(p) => p.theme.space("large")}
    ${(p) => p.theme.space("large")};
`;

const CategoryListBox = styled(ListBox)`
  display: flex;
  flex-direction: column;
  width: 100%;
  outline: none;
`;

const CategoryItem = styled(ListBoxItem)`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.space("normal")};
  padding: ${(p) => p.theme.space("normal")} ${(p) => p.theme.space("xsmall")};
  border-bottom: 1px solid ${(p) => p.theme.colors.lightNeutral};
  border-radius: ${(p) => p.theme.radius("small")};
  cursor: pointer;
  outline: none;

  &:last-child {
    border-bottom: none;
  }

  &[data-hovered="true"],
  &[data-focused="true"] {
    background-color: ${(p) => p.theme.colors.neutralBackgroundHover};
  }

  &[data-selected="true"] {
    background-color: ${(p) => p.theme.colors.neutralBackground};
  }

  &[data-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FooterRow = styled(Stack)`
  width: 100%;
`;
